import { ethers } from "ethers";
import { karmaTokenABI } from "../constants/contractABIs";
// KUSD Stablecoin on Polygon
export const KUSD_TOKEN_ADDRESS = "0x2032F19922EFeEf22Eb1E6A2AFbfC5fE4452B368";
export const KUSD_TOKEN_DECIMALS = 6;

// NOTE: transaction history here comes from the explorer APIs below, not from
// a JSON-RPC provider. A stale comment that embedded an Infura project key was
// removed: a credential must live in Infura/Agent Vault, never in source, and no
// public RPC is used anywhere in this module.
// Guard: scripts/check-private-providers.mjs (Gas Town).

// Minimal ERC20 ABI for transfer and decimals
export const MINIMAL_ERC20_ABI = [
  "function transfer(address to, uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address owner) view returns (uint256)",
];

// Transaction type definition
export interface Transaction {
  hash: string;
  timeStamp: string;
  from: string;
  to: string;
  value: string;
  tokenName?: string;
  tokenSymbol?: string;
  tokenDecimals?: string;
  tokenID?: string; // Added for NFT transactions
  gasUsed: string;
  confirmations: string;
  isError: string;
  blockNumber: string;
  network: "ethereum" | "polygon" | "base";
  contractAddress?: string; // Added to help identify token transactions
}

// Define more specific types for API responses to avoid 'any'
interface EtherscanTx {
  hash: string;
  timeStamp: string;
  from: string;
  to: string;
  value: string;
  gasUsed: string;
  confirmations: string;
  isError: string;
  blockNumber: string;
  contractAddress?: string;
  tokenName?: string;
  tokenSymbol?: string;
  tokenDecimal?: string;
  tokenID?: string; // Added for NFT transactions
}

interface EtherscanApiResponse {
  status: string;
  message: string;
  result: EtherscanTx[];
}

export interface ScannerRequest {
  network: "ethereum" | "polygon";
  action: "txlist" | "tokentx" | "tokennfttx";
  address: string;
  contractAddress?: string;
  page?: number;
  offset?: number;
  sort?: "asc" | "desc";
}

export type ScannerFetch = (
  request: ScannerRequest,
) => Promise<EtherscanApiResponse | null>;

export type ChainTransactionFetch = (request: {
  address: string;
  refresh?: boolean;
}) => Promise<{
  success?: boolean;
  transactions?: Transaction[];
  total?: number;
} | null>;

const FETCH_COUNT_PER_SOURCE = 25;

// NFT metadata shape retained for historical Game ID records.
export interface GameIdNftMetadata {
  name: string;
  description: string;
  image: string;
  external_url?: string;
  attributes: Array<{
    trait_type: string;
    value: string;
  }>;
  imageGatewayUrl?: string;
  imageIPFSHash?: string;
  contract_address?: string;
}

// Game ID NFT with retained historical metadata.
export interface GameIdNftWithMetadata {
  id: string;
  tokenURI: string;
  metadata: GameIdNftMetadata;
  owner: string;
  type: string;
  tokenAddress: string;
  chainId: number;
  ipfsData?: {
    image?: {
      ipfsHash: string;
      gatewayUrl: string;
      originalUrl: string;
    };
  } | null;
}

/**
 * Format a number as a currency string with specified decimal places
 */
export function formatCurrency(
  value: string | number,
  decimals: number = 2,
): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Get ETH balance for a wallet address
 */
export async function getEthBalance(
  provider: ethers.Provider,
  address: string,
): Promise<string> {
  try {
    const balance = await provider.getBalance(address);
    return ethers.formatEther(balance);
  } catch (error) {
    console.error("Error fetching ETH balance:", error);
    throw error;
  }
}

/**
 * Get token balance for a wallet address
 */
export async function getTokenBalance(
  walletAddress: string,
  provider: ethers.Provider,
  tokenAddress: string = KUSD_TOKEN_ADDRESS,
): Promise<string> {
  try {
    // Create contract interface
    const tokenAbi = [
      // Only need balanceOf function
      "function balanceOf(address owner) view returns (uint256)",
    ];
    const tokenContract = new ethers.Contract(tokenAddress, tokenAbi, provider);

    // Get token balance - assuming 18 decimals (standard for most ERC20 tokens)
    const balance = await tokenContract.balanceOf(walletAddress);
    return ethers.formatUnits(balance, 18);
  } catch (error) {
    console.error("Error fetching token balance:", error);
    throw new Error("CHAIN_DATA_UNAVAILABLE");
  }
}

/**
 * Get KARMA token balance for a wallet address
 */
export async function getKarmaBalance(
  provider: ethers.Provider,
  walletAddress: string,
): Promise<string> {
  const karmaTokenAddress = import.meta.env.VITE_PREDICT_TOKEN_ADDRESS;

  if (!karmaTokenAddress) {
    console.error(
      "KARMA Token Address (VITE_PREDICT_TOKEN_ADDRESS) is not configured.",
    );
    return "0";
  }
  if (!ethers.isAddress(karmaTokenAddress)) {
    console.error(
      "Invalid KARMA token address configured (VITE_PREDICT_TOKEN_ADDRESS):",
      karmaTokenAddress,
    );
    return "0";
  }
  if (!ethers.isAddress(walletAddress)) {
    console.error(
      "Invalid wallet address provided to getKarmaBalance:",
      walletAddress,
    );
    return "0";
  }

  try {
    // Debug: Check what network the provider is connected to
    const network = await provider.getNetwork();
    console.log("[getKarmaBalance] Provider network info:", {
      chainId: network.chainId.toString(),
      name: network.name,
      expectedChainId: "1",
    });

    const tokenContract = new ethers.Contract(
      karmaTokenAddress,
      MINIMAL_ERC20_ABI, // Using minimal ABI with balanceOf
      provider,
    );

    console.log("[getKarmaBalance] About to call balanceOf with:", {
      contractAddress: karmaTokenAddress,
      walletAddress: walletAddress,
    });

    const balance = await tokenContract.balanceOf(walletAddress);

    console.log("[getKarmaBalance] Raw balance response:", {
      balance: balance.toString(),
      type: typeof balance,
    });

    const formattedBalance = ethers.formatUnits(balance, 18);
    console.log("[getKarmaBalance] Formatted balance:", formattedBalance);

    return formattedBalance;
  } catch (error) {
    console.error("Error fetching KARMA balance:", error);
    console.error("Error details:", {
      message: error instanceof Error ? error.message : "Unknown error",
      code: (error as unknown as { code?: string | number })?.code,
      data: (error as unknown as { data?: unknown })?.data,
      reason: (error as unknown as { reason?: string })?.reason,
    });
    throw new Error("CHAIN_DATA_UNAVAILABLE");
  }
}

/**
 * Get KARMA token balance for a wallet address on Polygon network
 * Provider must be connected to Polygon network
 */
export async function getKarmaBalancePolygon(
  provider: ethers.Provider,
  walletAddress: string,
): Promise<string> {
  const karmaTokenAddress = import.meta.env.VITE_POLY_KARMA_CHILD_ADDR;

  if (!karmaTokenAddress) {
    console.error(
      "Polygon KARMA Token Address (VITE_POLY_KARMA_CHILD_ADDR) is not configured.",
    );
    return "0";
  }
  if (!ethers.isAddress(karmaTokenAddress)) {
    console.error(
      "Invalid Polygon KARMA token address configured (VITE_POLY_KARMA_CHILD_ADDR):",
      karmaTokenAddress,
    );
    return "0";
  }
  if (!ethers.isAddress(walletAddress)) {
    console.error(
      "Invalid wallet address provided to getKarmaBalancePolygon:",
      walletAddress,
    );
    return "0";
  }

  try {
    // Debug: Check what network the provider is connected to
    const network = await provider.getNetwork();
    console.log("[getKarmaBalancePolygon] Provider network info:", {
      chainId: network.chainId.toString(),
      name: network.name,
      expectedChainId: "137",
    });

    // Verify we're on Polygon mainnet
    if (network.chainId !== 137n) {
      console.warn(
        `[getKarmaBalancePolygon] Provider not on Polygon mainnet. Current chain: ${network.chainId}, expected: 137`,
      );
      // Still try the call, but log the warning
    }

    // Create a contract instance for the Polygon child KARMA token
    const tokenContract = new ethers.Contract(
      karmaTokenAddress,
      MINIMAL_ERC20_ABI,
      provider,
    );

    console.log("[getKarmaBalancePolygon] About to call balanceOf with:", {
      contractAddress: karmaTokenAddress,
      walletAddress: walletAddress,
    });

    const balance = await tokenContract.balanceOf(walletAddress);

    console.log("[getKarmaBalancePolygon] Raw balance response:", {
      balance: balance.toString(),
      type: typeof balance,
    });

    const formattedBalance = ethers.formatUnits(balance, 18);
    console.log(
      "[getKarmaBalancePolygon] Formatted balance:",
      formattedBalance,
    );

    return formattedBalance;
  } catch (error) {
    console.error("Error fetching KARMA balance on Polygon:", error);
    console.error("Error details:", {
      message: error instanceof Error ? error.message : "Unknown error",
      code: (error as unknown as { code?: string | number })?.code,
      data: (error as unknown as { data?: unknown })?.data,
      reason: (error as unknown as { reason?: string })?.reason,
    });
    throw new Error("CHAIN_DATA_UNAVAILABLE");
  }
}

/**
 * Get KARMA token balance for a wallet address on Base network
 * Provider must be connected to Base network
 * Added 2026-01-30 for Base chain migration
 */
export async function getKarmaBalanceBase(
  provider: ethers.Provider,
  walletAddress: string,
): Promise<string> {
  // Use new env var with fallback to new Base address
  const karmaTokenAddress =
    import.meta.env.VITE_KARMA_TOKEN_ADDRESS ||
    "0x0910b5224d0d89f58885d0aeb20d56d1870127cf";

  if (!ethers.isAddress(karmaTokenAddress)) {
    console.error(
      "Invalid Base KARMA token address configured (VITE_KARMA_TOKEN_ADDRESS):",
      karmaTokenAddress,
    );
    return "0";
  }
  if (!ethers.isAddress(walletAddress)) {
    console.error(
      "Invalid wallet address provided to getKarmaBalanceBase:",
      walletAddress,
    );
    return "0";
  }

  try {
    // Debug: Check what network the provider is connected to
    const network = await provider.getNetwork();
    console.log("[getKarmaBalanceBase] Provider network info:", {
      chainId: network.chainId.toString(),
      name: network.name,
      expectedChainId: "8453",
    });

    // Verify we're on Base mainnet
    if (network.chainId !== 8453n) {
      console.warn(
        `[getKarmaBalanceBase] Provider not on Base mainnet. Current chain: ${network.chainId}, expected: 8453`,
      );
      // Still try the call, but log the warning
    }

    // Create a contract instance for the Base KARMA token
    const tokenContract = new ethers.Contract(
      karmaTokenAddress,
      MINIMAL_ERC20_ABI,
      provider,
    );

    console.log("[getKarmaBalanceBase] About to call balanceOf with:", {
      contractAddress: karmaTokenAddress,
      walletAddress: walletAddress,
    });

    const balance = await tokenContract.balanceOf(walletAddress);

    console.log("[getKarmaBalanceBase] Raw balance response:", {
      balance: balance.toString(),
      type: typeof balance,
    });

    const formattedBalance = ethers.formatUnits(balance, 18);
    console.log("[getKarmaBalanceBase] Formatted balance:", formattedBalance);

    return formattedBalance;
  } catch (error) {
    console.error("Error fetching KARMA balance on Base:", error);
    console.error("Error details:", {
      message: error instanceof Error ? error.message : "Unknown error",
      code: (error as unknown as { code?: string | number })?.code,
      data: (error as unknown as { data?: unknown })?.data,
      reason: (error as unknown as { reason?: string })?.reason,
    });
    throw new Error("CHAIN_DATA_UNAVAILABLE");
  }
}

/**
 * Get combined KARMA token balance from both Ethereum and Base networks
 * Migrated from Polygon to Base 2026-01-30
 */
export async function getCombinedKarmaBalance(
  ethProvider: ethers.Provider,
  baseProvider: ethers.Provider,
  walletAddress: string,
): Promise<{ ethereum: string; base: string; total: string }> {
  try {
    const [ethBalance, baseBalance] = await Promise.all([
      getKarmaBalance(ethProvider, walletAddress),
      getKarmaBalanceBase(baseProvider, walletAddress),
    ]);

    const ethNum = parseFloat(ethBalance) || 0;
    const baseNum = parseFloat(baseBalance) || 0;
    const total = (ethNum + baseNum).toString();

    return {
      ethereum: ethBalance,
      base: baseBalance,
      total,
    };
  } catch (error) {
    console.error("Error fetching combined KARMA balance:", error);
    return {
      ethereum: "0",
      base: "0",
      total: "0",
    };
  }
}

/**
 * Get KUSD token balance for a wallet address from Polygon network
 * Provider must be connected to Polygon network
 */
export async function getKusdBalance(
  provider: ethers.Provider,
  walletAddress: string,
): Promise<string> {
  if (!ethers.isAddress(walletAddress)) {
    console.error(
      "Invalid wallet address provided to getKusdBalance:",
      walletAddress,
    );
    return "0";
  }
  // KUSD_TOKEN_ADDRESS is a const, so no need to check if it's configured here, assuming it's valid.

  try {
    // Ensure the provider is connected to Polygon (optional check, relies on component to ensure this)
    // const network = await provider.getNetwork();
    // if (network.chainId !== 137) { // 137 is Polygon Mainnet chain ID
    // 	console.warn('Provider not connected to Polygon when fetching KUSD balance.');
    //  // Depending on desired behavior, could return '0' or throw error
    // }

    const tokenContract = new ethers.Contract(
      KUSD_TOKEN_ADDRESS,
      MINIMAL_ERC20_ABI,
      provider,
    );
    const balance = await tokenContract.balanceOf(walletAddress);
    return ethers.formatUnits(balance, KUSD_TOKEN_DECIMALS);
  } catch (error) {
    console.error("Error fetching KUSD balance:", error);
    throw new Error("CHAIN_DATA_UNAVAILABLE");
  }
}

/**
 * Send KARMA tokens to a recipient
 */
export async function sendKarma(
  signer: ethers.Signer,
  recipientAddress: string,
  amount: string,
  tokenAddress: string,
): Promise<string> {
  try {
    // Validate the recipient address
    if (!ethers.isAddress(recipientAddress)) {
      throw new Error("Invalid recipient address");
    }

    // Validate the amount
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      throw new Error("Invalid amount");
    }

    const tokenContract = new ethers.Contract(
      tokenAddress,
      karmaTokenABI,
      signer,
    );

    const signerAddress = await signer.getAddress();
    const balance = await tokenContract.balanceOf(signerAddress);
    const amountInWei = ethers.parseUnits(amount, 18); // Assuming 18 decimals

    // Check if the sender has enough balance
    if (balance < amountInWei) {
      throw new Error("Insufficient token balance");
    }

    // Send the transaction
    const tx = await tokenContract.transfer(recipientAddress, amountInWei);

    // Wait for the transaction to be mined
    const receipt = await tx.wait();
    return receipt.hash;
  } catch (error) {
    console.error("Error sending KARMA tokens:", error);
    throw error;
  }
}

/**
 * Send KUSD tokens to a recipient on Polygon network
 */
export async function sendKusd(
  signer: ethers.Signer, // Signer should be connected to Polygon RPC
  recipientAddress: string,
  amount: string, // Amount in KUSD (e.g., "0.10")
): Promise<string> {
  try {
    // Validate the recipient address
    if (!ethers.isAddress(recipientAddress)) {
      throw new Error("Invalid recipient address");
    }

    // Validate the amount
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      throw new Error("Invalid amount: must be a positive number");
    }

    const tokenContract = new ethers.Contract(
      KUSD_TOKEN_ADDRESS,
      MINIMAL_ERC20_ABI, // Using the minimal ABI
      signer,
    );

    // KUSD has 6 decimals
    const amountInSmallestUnit = ethers.parseUnits(amount, KUSD_TOKEN_DECIMALS);

    // Optional: Check if the sender has enough balance (requires signer to have KUSD)
    // const signerAddress = await signer.getAddress();
    // const balance = await tokenContract.balanceOf(signerAddress);
    // if (balance < amountInSmallestUnit) {
    // throw new Error('Insufficient KUSD balance');
    // }

    // Send the transaction
    const tx = await tokenContract.transfer(
      recipientAddress,
      amountInSmallestUnit,
    );

    // Wait for the transaction to be mined
    // For Polygon, 1 confirmation is usually enough for faster feedback, but more can be used for higher certainty.
    const receipt = await tx.wait(1);
    if (!receipt || !receipt.hash) {
      throw new Error("Transaction receipt not found or hash missing.");
    }
    return receipt.hash;
  } catch (error) {
    console.error("Error sending KUSD tokens:", error);
    // Rethrow the error so the caller can handle it
    throw error instanceof Error ? error : new Error(String(error));
  }
}

async function fetchScannerJson<T>(
  request: ScannerRequest,
  label: string,
  scannerFetch?: ScannerFetch,
): Promise<T | null> {
  try {
    if (!scannerFetch) {
      console.debug(
        `Optional scanner history lookup skipped for ${label}: server scanner fetcher unavailable`,
      );
      return null;
    }

    return (await scannerFetch(request)) as T | null;
  } catch (error) {
    console.debug(
      `Optional scanner history lookup failed for ${label}:`,
      error,
    );
    return null;
  }
}

/**
 * Fetch transaction history for a wallet address from Etherscan/Polygonscan.
 * This function fetches a batch of recent transactions from each relevant source (ETH, KARMA, KUSD, NFT),
 * combines them, sorts them by timestamp, and returns the entire sorted list.
 * The component calling this function will be responsible for UI pagination.
 *
 * @param address Wallet address to fetch transactions for
 * @param transactionTypes Array of transaction types to fetch ('eth', 'karma', 'kusd', 'nft')
 * @returns Promise with an object containing:
 *  - `result`: An array of all fetched, combined, and sorted transactions.
 *  - `hasMoreFromSource`: Boolean indicating if any of the underlying APIs might have more data than fetched.
 */
export async function getTransactionHistory(
  address: string,
  transactionTypes: Array<"eth" | "karma" | "kusd" | "nft"> = ["eth", "karma"],
  options: {
    refresh?: boolean;
    scannerFetch?: ScannerFetch;
    chainTransactionFetch?: ChainTransactionFetch;
  } = {},
): Promise<{ result: Transaction[]; hasMoreFromSource: boolean }> {
  if (transactionTypes.some(type => type !== "eth") && !options.chainTransactionFetch) {
    throw new Error("CHAIN_DATA_UNAVAILABLE");
  }
  // Separate contract addresses for each network
  const karmaEthContractAddress = (
    import.meta.env.VITE_PREDICT_TOKEN_ADDRESS || ""
  ).toLowerCase();
  const karmaPolygonContractAddress = (
    import.meta.env.VITE_POLY_KARMA_CHILD_ADDR || ""
  ).toLowerCase();

  // console.log('🔑 WH: PolygonScan API Key:', polygonscanApiKey); // WH for wallet-helpers

  let allTransactions: Transaction[] = [];
  let hasMoreFromAnySource = false;

  try {
    // Fetch Ethereum (ETH & KARMA) transactions
    if (
      transactionTypes.includes("eth") ||
      transactionTypes.includes("karma")
    ) {
      // Fetch normal ETH transactions
      const ethData = await fetchScannerJson<EtherscanApiResponse>(
        {
          network: "ethereum",
          action: "txlist",
          address,
          page: 1,
          offset: FETCH_COUNT_PER_SOURCE,
          sort: "desc",
        },
        "ethereum native",
        options.scannerFetch,
      );
      const normalTxs: Transaction[] =
        ethData?.status === "1" && Array.isArray(ethData.result)
          ? ethData.result.map((tx: EtherscanTx) => ({
              ...tx,
              network: "ethereum" as const,
            }))
          : [];
      if (normalTxs.length === FETCH_COUNT_PER_SOURCE)
        hasMoreFromAnySource = true;

      // Fetch ERC20 token transfers (KARMA on Ethereum)
      const karmaData = await fetchScannerJson<EtherscanApiResponse>(
        {
          network: "ethereum",
          action: "tokentx",
          address,
          page: 1,
          offset: FETCH_COUNT_PER_SOURCE,
          sort: "desc",
        },
        "ethereum token",
        options.scannerFetch,
      );
      let karmaTxs: Transaction[] =
        karmaData?.status === "1" && Array.isArray(karmaData.result)
          ? karmaData.result
              // Initial filter for contract address or symbol if only KARMA is requested early
              .filter((tx: EtherscanTx) => {
                if (
                  transactionTypes.includes("karma") &&
                  !transactionTypes.includes("eth")
                ) {
                  // Only Karma requested from ERC20
                  if (!karmaEthContractAddress) return true; // if no address, don't filter by it specifically
                  return (
                    tx.tokenSymbol?.toUpperCase() === "KARMA" || // Keep symbol check as a fallback
                    tx.contractAddress?.toLowerCase() ===
                      karmaEthContractAddress
                  );
                }
                return true; // If ETH is also requested, or neither specifically, include all ERC20 for now
              })
              .map((tx: EtherscanTx) => ({
                ...tx,
                tokenDecimals: tx.tokenDecimal,
                network: "ethereum" as const,
              }))
          : [];

      // Refined filtering for Karma vs other ERC20s
      if (
        transactionTypes.includes("eth") &&
        !transactionTypes.includes("karma")
      ) {
        // Only ETH (and non-KARMA ERC20s) requested
        karmaTxs = karmaTxs.filter(
          (tx) =>
            !(
              tx.tokenSymbol?.toUpperCase() === "KARMA" || // Keep symbol check
              (karmaEthContractAddress &&
                tx.contractAddress?.toLowerCase() === karmaEthContractAddress)
            ),
        );
      } else if (
        !transactionTypes.includes("eth") &&
        transactionTypes.includes("karma")
      ) {
        // Only KARMA ERC20s requested, ETH native not requested
        karmaTxs = karmaTxs.filter(
          (tx) =>
            tx.tokenSymbol?.toUpperCase() === "KARMA" || // Keep symbol check
            (karmaEthContractAddress &&
              tx.contractAddress?.toLowerCase() === karmaEthContractAddress),
        );
        // If only Karma is requested, normalTxs (ETH native) should not be added
        normalTxs.length = 0;
      } else if (
        !transactionTypes.includes("eth") &&
        !transactionTypes.includes("karma")
      ) {
        // Neither ETH nor Karma requested from Ethereum side. Clear both.
        normalTxs.length = 0;
        karmaTxs.length = 0;
      }
      // If both ETH and Karma are requested, both normalTxs and fully filtered karmaTxs are included.

      if (
        karmaTxs.length === FETCH_COUNT_PER_SOURCE &&
        transactionTypes.includes("karma")
      ) {
        hasMoreFromAnySource = true;
      }
      allTransactions = allTransactions.concat(normalTxs, karmaTxs);
    }

    // Fetch NFT transactions from Ethereum
    if (transactionTypes.includes("nft")) {
      const nftEthData = await fetchScannerJson<EtherscanApiResponse>(
        {
          network: "ethereum",
          action: "tokennfttx",
          address,
          page: 1,
          offset: FETCH_COUNT_PER_SOURCE,
          sort: "desc",
        },
        "ethereum nft",
        options.scannerFetch,
      );
      const nftEthTxs: Transaction[] =
        nftEthData?.status === "1" && Array.isArray(nftEthData.result)
          ? nftEthData.result.map((tx: EtherscanTx) => ({
              ...tx,
              tokenSymbol: tx.tokenSymbol || "NFT",
              tokenName: tx.tokenName || "NFT",
              tokenDecimals: tx.tokenDecimal || "0",
              network: "ethereum" as const,
            }))
          : [];

      if (nftEthTxs.length === FETCH_COUNT_PER_SOURCE)
        hasMoreFromAnySource = true;
      allTransactions = allTransactions.concat(nftEthTxs);
    }

    // Fetch KUSD transactions from Polygon
    if (transactionTypes.includes("kusd")) {
      const kusdData = await fetchScannerJson<EtherscanApiResponse>(
        {
          network: "polygon",
          action: "tokentx",
          address,
          contractAddress: KUSD_TOKEN_ADDRESS,
          page: 1,
          offset: FETCH_COUNT_PER_SOURCE,
          sort: "desc",
        },
        "polygon kusd",
        options.scannerFetch,
      );
      // console.log('📄 WH: KUSD API Response Data:', JSON.parse(JSON.stringify(kusdData)));

      const kusdTxs: Transaction[] =
        kusdData?.status === "1" && Array.isArray(kusdData.result)
          ? kusdData.result.map((tx: EtherscanTx) => ({
              ...tx,
              tokenSymbol: tx.tokenSymbol || "KUSD",
              tokenDecimals: tx.tokenDecimal || String(KUSD_TOKEN_DECIMALS),
              network: "polygon" as const,
            }))
          : [];
      // console.log('💵 WH: Processed KUSD Transactions:', JSON.parse(JSON.stringify(kusdTxs)));

      if (kusdTxs.length === FETCH_COUNT_PER_SOURCE)
        hasMoreFromAnySource = true;
      allTransactions = allTransactions.concat(kusdTxs);
    }

    // Fetch KARMA transactions from Polygon (using child contract address)
    if (transactionTypes.includes("karma") && karmaPolygonContractAddress) {
      const karmaPolygonData = await fetchScannerJson<EtherscanApiResponse>(
        {
          network: "polygon",
          action: "tokentx",
          address,
          contractAddress: karmaPolygonContractAddress,
          page: 1,
          offset: FETCH_COUNT_PER_SOURCE,
          sort: "desc",
        },
        "polygon karma",
        options.scannerFetch,
      );

      const karmaPolygonTxs: Transaction[] =
        karmaPolygonData?.status === "1" &&
        Array.isArray(karmaPolygonData.result)
          ? karmaPolygonData.result.map((tx: EtherscanTx) => ({
              ...tx,
              tokenSymbol: tx.tokenSymbol || "KARMA",
              tokenDecimals: tx.tokenDecimal || "18",
              network: "polygon" as const,
            }))
          : [];

      if (karmaPolygonTxs.length === FETCH_COUNT_PER_SOURCE)
        hasMoreFromAnySource = true;
      allTransactions = allTransactions.concat(karmaPolygonTxs);
    }

    // Fetch NFT transactions from Polygon
    if (transactionTypes.includes("nft")) {
      const nftPolygonData = await fetchScannerJson<EtherscanApiResponse>(
        {
          network: "polygon",
          action: "tokennfttx",
          address,
          page: 1,
          offset: FETCH_COUNT_PER_SOURCE,
          sort: "desc",
        },
        "polygon nft",
        options.scannerFetch,
      );
      const nftPolygonTxs: Transaction[] =
        nftPolygonData?.status === "1" && Array.isArray(nftPolygonData.result)
          ? nftPolygonData.result.map((tx: EtherscanTx) => ({
              ...tx,
              tokenSymbol: tx.tokenSymbol || "NFT",
              tokenName: tx.tokenName || "NFT",
              tokenDecimals: tx.tokenDecimal || "0",
              network: "polygon" as const,
            }))
          : [];

      if (nftPolygonTxs.length === FETCH_COUNT_PER_SOURCE)
        hasMoreFromAnySource = true;
      allTransactions = allTransactions.concat(nftPolygonTxs);
    }

    const serverChainTransactions =
      options.chainTransactionFetch &&
      (transactionTypes.includes("karma") ||
        transactionTypes.includes("kusd") ||
        transactionTypes.includes("nft"))
        ? await options
            .chainTransactionFetch({
              address,
              refresh: options.refresh,
            })
            .catch(() => null)
        : null;

    // The server owns chain-data availability. Failure is not an empty wallet
    // and must not fall back to retired browser Insight or enter the UI cache.
    if (options.chainTransactionFetch &&
        transactionTypes.some((type) => type !== "eth") &&
        (serverChainTransactions?.success !== true ||
         !Array.isArray(serverChainTransactions.transactions))) {
      throw new Error("CHAIN_DATA_UNAVAILABLE");
    }

    if (Array.isArray(serverChainTransactions?.transactions)) {
      allTransactions = allTransactions.concat(
        serverChainTransactions.transactions.filter((tx) => {
          const symbol = tx.tokenSymbol?.toUpperCase();
          const isKarma = symbol === "KARMA" || symbol === "WKRM";
          const isKusd = symbol === "KUSD";
          const isNft =
            tx.tokenDecimals === "0" ||
            ["GAME ID", "GAME ID (POLY)", "BUILDER", "ACCESS", "ORACLE", "PLAYER", "NFT"].includes(
              symbol || "",
            );
          return (
            (transactionTypes.includes("karma") && isKarma) ||
            (transactionTypes.includes("kusd") && isKusd) ||
            (transactionTypes.includes("nft") && isNft)
          );
        }),
      );
    }

    // console.log('📋 WH: All raw fetched transactions (before sort/dedupe):', JSON.parse(JSON.stringify(allTransactions)));

    // Create a Map to deduplicate by hash (network-aware)
    const txMap = new Map<string, Transaction>();
    allTransactions.forEach((tx) => {
      const mapKey = [
        tx.network,
        tx.hash,
        tx.contractAddress?.toLowerCase() || "native",
        tx.tokenID || "",
        tx.tokenSymbol || "",
      ].join("-");
      const existing = txMap.get(mapKey);
      // Prioritize token transactions if a generic (ETH) and token (KARMA) tx share a hash on the same network
      // (though this specific scenario, same hash for ETH native and ERC20, is unlikely)
      // More importantly, ensure we have the most complete data if for some reason duplicates exist from API.
      if (
        !existing ||
        (tx.tokenSymbol && !existing.tokenSymbol) ||
        (!existing.tokenSymbol && tx.tokenSymbol)
      ) {
        txMap.set(mapKey, tx);
      } else if (existing && tx.tokenSymbol && existing.tokenSymbol) {
        // If both are token transactions, prefer the one with more details or simply the latest processed one (current logic)
        txMap.set(mapKey, tx);
      } else if (
        !existing.tokenSymbol &&
        !tx.tokenSymbol &&
        existing.network === tx.network
      ) {
        // If both are native transactions on the same network, keep the one already there or update (current logic updates)
        txMap.set(mapKey, tx);
      }
    });

    const combinedSortedTransactions = Array.from(txMap.values()).sort(
      (a, b) => parseInt(b.timeStamp) - parseInt(a.timeStamp),
    );
    // console.log('📑 WH: Combined sorted/deduped (before pagination):', JSON.parse(JSON.stringify(combinedSortedTransactions)));

    // This function now returns the whole sorted list. UI handles pagination.
    // The 'hasMoreFromSource' indicates if any API might have more data beyond what we fetched.
    // console.log('✂️ WH: Paginated result (final to component):', JSON.parse(JSON.stringify(paginatedResult))); // No longer paginating here

    return {
      result: combinedSortedTransactions,
      hasMoreFromSource: hasMoreFromAnySource,
    };
  } catch (error) {
    console.error("Error fetching transaction history:", error);
    throw new Error("CHAIN_DATA_UNAVAILABLE");
  }
}

/**
 * Estimate the gas fee for sending an ERC20 token transaction on Ethereum
 *
 * @param provider Ethereum provider
 * @param signer Signer to use for the transaction
 * @param tokenAddress Token contract address
 * @param recipientAddress Recipient address
 * @param amount Amount to send in token units (as string)
 * @param decimals Token decimals
 * @returns Promise with estimated gas fee in ETH as string
 */
export async function estimateTokenTransferGas(
  provider: ethers.Provider,
  signer: ethers.Signer,
  tokenAddress: string,
  recipientAddress: string,
  amount: string,
  decimals: number,
): Promise<string> {
  try {
    if (!ethers.isAddress(recipientAddress)) {
      throw new Error("Invalid recipient address");
    }

    const tokenContract = new ethers.Contract(
      tokenAddress,
      MINIMAL_ERC20_ABI,
      signer, // Contract instance uses signer, which dictates network for estimateGas
    );

    const amountInSmallestUnit = ethers.parseUnits(amount || "0", decimals);

    // Estimate gas for the transaction
    const gasEstimate = await tokenContract.transfer.estimateGas(
      recipientAddress,
      amountInSmallestUnit,
    );

    // Get current gas price from the provided Ethereum provider
    const feeData = await provider.getFeeData();
    const gasPrice = feeData.gasPrice || ethers.parseUnits("50", "gwei"); // Fallback gas price

    // Calculate fee
    const fee = gasEstimate * gasPrice;
    return ethers.formatEther(fee);
  } catch (error) {
    console.error("Error estimating gas fee:", error);
    return "0.0001"; // Return fallback estimate
  }
}

/**
 * Estimate the gas fee for sending KUSD tokens on Polygon network
 *
 * @param signer Signer to use for the transaction (must be connected to Polygon)
 * @param recipientAddress Recipient address
 * @param amount Amount to send in KUSD units (as string)
 * @returns Promise with estimated gas fee in MATIC as string
 */
export async function estimateKusdTokenTransferGas(
  signer: ethers.Signer, // Signer must be connected to Polygon
  recipientAddress: string,
  amount: string,
): Promise<string> {
  try {
    if (!ethers.isAddress(recipientAddress)) {
      throw new Error("Invalid recipient address for KUSD transfer");
    }

    if (!signer.provider) {
      console.error(
        "Cannot estimate KUSD gas: Signer doesn't have a provider.",
      );
      // Using a distinct fallback to indicate this specific issue if it occurs
      return "0.0002"; // Fallback estimate in MATIC
    }

    const kusdContract = new ethers.Contract(
      KUSD_TOKEN_ADDRESS,
      MINIMAL_ERC20_ABI,
      signer, // Signer is used for estimateGas, should be on Polygon
    );

    const amountInSmallestUnit = ethers.parseUnits(
      amount || "0",
      KUSD_TOKEN_DECIMALS,
    );

    const gasEstimate = await kusdContract.transfer.estimateGas(
      recipientAddress,
      amountInSmallestUnit,
    );

    // Get current gas price from Signer's provider
    const feeData = await signer.provider.getFeeData();
    const gasPrice = feeData.gasPrice;

    if (!gasPrice) {
      // Fallback if gasPrice is null
      console.warn(
        "KUSD Gas Price: Could not fetch gas price from signer's provider, using fallback.",
      );
      const fallbackGasPrice = ethers.parseUnits("100", "gwei"); // Example fallback for Polygon
      const fee = gasEstimate * fallbackGasPrice;
      return ethers.formatEther(fee);
    }

    const fee = gasEstimate * gasPrice;
    return ethers.formatEther(fee);
  } catch (error) {
    console.error("Error estimating KUSD gas fee:", error);
    // Provide a slightly more relevant fallback for Polygon if possible, though '0.0001' MATIC might be too low or high.
    // For consistency with the other estimate function:
    return "0.0001"; // Return fallback estimate (in MATIC)
  }
}

/**
 * Get USD prices for cryptocurrencies
 *
 * @param cryptos Array of crypto symbols to get prices for (e.g. ['ETH', 'BTC'])
 * @returns Promise with price data
 */
export async function getCryptoUsdPrices(
  cryptos: string[] = ["ETH"],
): Promise<Record<string, number>> {
  try {
    // Use CoinGecko API for prices
    const cryptoIds = {
      ETH: "ethereum",
      BTC: "bitcoin",
    };

    const idString = cryptos
      .map((crypto) => cryptoIds[crypto as keyof typeof cryptoIds] || "")
      .filter(Boolean)
      .join(",");

    const response = await fetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${idString}&vs_currencies=usd`,
    );

    const data = await response.json();

    // Convert response to a simpler format
    const prices: Record<string, number> = {};

    cryptos.forEach((crypto) => {
      const id = cryptoIds[crypto as keyof typeof cryptoIds];
      if (id && data[id]) {
        prices[crypto] = data[id].usd;
      } else {
        prices[crypto] = 0;
      }
    });

    return prices;
  } catch (error) {
    console.error("Error fetching crypto prices:", error);
    return cryptos.reduce(
      (acc, crypto) => {
        acc[crypto] = 0;
        return acc;
      },
      {} as Record<string, number>,
    );
  }
}
